import React, { useState, useEffect } from 'react';
import { api, getStation } from '../api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const CONTENT_TYPES = [
  { id:'music',   label:'Music',         icon:'🎵' },
  { id:'talk',    label:'Talk',          icon:'🎙' },
  { id:'news',    label:'News',          icon:'📰' },
  { id:'persona', label:'DBP Persona',   icon:'🤖' },
];
const CO_ANCHOR_MODES = [
  { id:'alternating',   label:'Alternating',    desc:'Equal airtime — trade every segment' },
  { id:'call_response', label:'Call & Response', desc:'One sets up, one responds' },
  { id:'lead_support',  label:'Lead & Support',  desc:'One leads, one adds color' },
  { id:'human_ai',      label:'Human + AI',      desc:'Live human anchor with AI co-host' },
];

function Btn({ children, onClick, variant='primary', size='md', disabled, style }) {
  const base = { border:'none', borderRadius:9, fontWeight:600,
    cursor: disabled?'not-allowed':'pointer', opacity:disabled?0.5:1,
    transition:'all 0.15s', ...style };
  const sz  = size==='sm' ? {padding:'6px 14px',fontSize:12} : {padding:'10px 20px',fontSize:13};
  const col = variant==='primary'  ? {background:'#6366f1',color:'#fff'}
            : variant==='danger'   ? {background:'#fee2e2',color:'#dc2626'}
            : variant==='success'  ? {background:'#dcfce7',color:'#16a34a'}
            :                         {background:'#f1f5f9',color:'#374151'};
  return <button onClick={onClick} disabled={disabled} style={{...base,...sz,...col}}>{children}</button>;
}

function Card({ children, style }) {
  return (
    <div style={{ background:'#fff', borderRadius:14, border:'1px solid #f1f5f9',
      boxShadow:'0 1px 4px rgba(0,0,0,0.05)', padding:20, ...style }}>
      {children}
    </div>
  );
}

function Select({ label, value, onChange, options, style }) {
  return (
    <div style={{ marginBottom:14, ...style }}>
      {label && <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:5 }}>{label}</div>}
      <select value={value} onChange={e=>onChange(e.target.value)}
        style={{ width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8,
          fontSize:13, background:'#fff', cursor:'pointer', outline:'none' }}>
        {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
      </select>
    </div>
  );
}

function Input({ label, value, onChange, type='text', placeholder, style }) {
  return (
    <div style={{ marginBottom:14, ...style }}>
      {label && <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:5 }}>{label}</div>}
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{ width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:8,
          fontSize:13, outline:'none', boxSizing:'border-box' }} />
    </div>
  );
}

// ─── Program Form Modal ───────────────────────────────────────────────────────

function ProgramModal({ station, personas, editProgram, onClose, onSaved }) {
  const isEdit = !!editProgram;
  const [form, setForm] = useState({
    name: editProgram?.name || '',
    content_type: editProgram?.content_type || 'music',
    day_of_week: editProgram?.day_of_week ?? 0,
    start_time: editProgram?.start_time || '06:00',
    end_time: editProgram?.end_time || '10:00',
    persona_id: editProgram?.persona_id || '',
    co_anchor_persona_id: editProgram?.co_anchor_persona_id || '',
    co_anchor_mode: editProgram?.co_anchor_mode || 'alternating',
    human_name: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const isPersonaMode = form.content_type === 'persona';
  const isHumanAI = form.co_anchor_mode === 'human_ai';
  const hasCoAnchor = form.co_anchor_persona_id && form.co_anchor_mode !== 'human_ai';

  const save = async () => {
    if (!form.name) { setError('Program name required'); return; }
    if (isPersonaMode && !form.persona_id) { setError('Select a persona'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        name: form.name,
        content_type: form.content_type,
        day_of_week: parseInt(form.day_of_week),
        start_time: form.start_time,
        end_time: form.end_time,
        channel_id: station.channel_id || station.id,
        location_id: station.location_id || null,
        genres: [],
        mood: 'neutral',
        content_sequence: [],
      };
      if (isPersonaMode) {
        payload.persona_id = parseInt(form.persona_id);
      }

      let result;
      if (isEdit) {
        result = await api.put(`/radio-programs/${editProgram.id}`, payload);
      } else {
        result = await api.post('/radio-programs', payload);
      }

      // Assign co-anchor if set
      const programId = result?.program?.id || editProgram?.id;
      if (programId && isPersonaMode) {
        await api.put(`/broadcast/co-anchor/programs/${programId}`, {
          co_anchor_persona_id: form.co_anchor_persona_id ? parseInt(form.co_anchor_persona_id) : null,
          co_anchor_mode: form.co_anchor_mode,
        }).catch(() => {});
      }

      onSaved();
      onClose();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const personaOptions = [
    { value:'', label:'— Select persona —' },
    ...personas.map(p => ({ value: p.id, label: `${p.voice_name} (${p.status})` }))
  ];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:560, maxHeight:'90vh',
        overflowY:'auto', padding:0 }}>
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #f1f5f9', display:'flex',
          alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontWeight:700, color:'#0f172a' }}>{isEdit ? 'Edit' : 'New'} Program Slot</div>
          <Btn onClick={onClose} variant='ghost' size='sm'>✕</Btn>
        </div>
        <div style={{ padding:24 }}>
          <Input label="Program Name" value={form.name} onChange={v=>set('name',v)}
            placeholder="e.g. Morning Drive, Late Night Vibes" />

          {/* Content Type */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>Content Type</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
              {CONTENT_TYPES.map(ct=>(
                <button key={ct.id} onClick={()=>set('content_type',ct.id)} style={{
                  padding:'10px 6px', borderRadius:9, cursor:'pointer', textAlign:'center',
                  border:`2px solid ${form.content_type===ct.id ? '#6366f1' : '#e2e8f0'}`,
                  background: form.content_type===ct.id ? '#eef2ff' : '#fff' }}>
                  <div style={{ fontSize:20, marginBottom:3 }}>{ct.icon}</div>
                  <div style={{ fontSize:11, fontWeight:600,
                    color: form.content_type===ct.id ? '#6366f1' : '#374151' }}>{ct.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Schedule */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <Select label="Day" value={form.day_of_week} onChange={v=>set('day_of_week',v)}
              options={DAYS.map((d,i)=>({value:i,label:d}))} />
            <Input label="Start Time" type="time" value={form.start_time} onChange={v=>set('start_time',v)} />
            <Input label="End Time" type="time" value={form.end_time} onChange={v=>set('end_time',v)} />
          </div>

          {/* Persona section */}
          {isPersonaMode && (
            <div style={{ background:'#f8fafc', borderRadius:10, padding:16, marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:12 }}>🤖 DBP Persona Configuration</div>

              <Select label="Primary Persona" value={form.persona_id}
                onChange={v=>set('persona_id',v)} options={personaOptions} />

              <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>Co-Anchor Mode</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:14 }}>
                {CO_ANCHOR_MODES.map(m=>(
                  <button key={m.id} onClick={()=>set('co_anchor_mode',m.id)} style={{
                    padding:10, borderRadius:9, cursor:'pointer', textAlign:'left',
                    border:`2px solid ${form.co_anchor_mode===m.id ? '#6366f1' : '#e2e8f0'}`,
                    background: form.co_anchor_mode===m.id ? '#eef2ff' : '#fff' }}>
                    <div style={{ fontSize:12, fontWeight:700,
                      color: form.co_anchor_mode===m.id ? '#6366f1' : '#0f172a', marginBottom:2 }}>{m.label}</div>
                    <div style={{ fontSize:10, color:'#64748b' }}>{m.desc}</div>
                  </button>
                ))}
              </div>

              {/* Co-anchor persona (not needed for solo or human_ai) */}
              {form.co_anchor_mode !== 'human_ai' && (
                <Select label="Co-Anchor Persona (optional)"
                  value={form.co_anchor_persona_id}
                  onChange={v=>set('co_anchor_persona_id',v)}
                  options={[{value:'',label:'— Solo (no co-anchor) —'},
                    ...personas.filter(p=>p.id!==parseInt(form.persona_id))
                      .map(p=>({value:p.id,label:`${p.voice_name} (${p.status})`}))]} />
              )}

              {/* Human+AI config */}
              {isHumanAI && (
                <>
                  <Input label="Human Anchor Name" value={form.human_name}
                    onChange={v=>set('human_name',v)} placeholder="e.g. DJ Marcus" />
                  <div style={{ fontSize:11, color:'#64748b', background:'#eff6ff',
                    borderRadius:8, padding:'8px 10px' }}>
                    💡 In Human+AI mode, the AI responds to your live broadcasts in ~5-8 seconds.
                    Use the Generate panel to trigger AI responses during your show.
                  </div>
                </>
              )}

              {/* Co-anchor pricing preview */}
              {hasCoAnchor && (
                <div style={{ marginTop:12, background:'#f0fdf4', borderRadius:8, padding:10, fontSize:12, color:'#166534' }}>
                  🏷️ Co-anchor bundle: 15% discount applied to combined licensing cost
                </div>
              )}
            </div>
          )}

          {error && <div style={{ marginBottom:12, padding:10, background:'#fef2f2',
            borderRadius:8, fontSize:12, color:'#dc2626' }}>{error}</div>}

          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={onClose} variant='ghost' style={{ flex:1 }}>Cancel</Btn>
            <Btn onClick={save} disabled={saving} style={{ flex:2 }}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Program'}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Program Card ─────────────────────────────────────────────────────────────

function ProgramCard({ program, personas, onEdit, onDelete }) {
  const ct = CONTENT_TYPES.find(c=>c.id===program.content_type) || CONTENT_TYPES[0];
  const persona = personas.find(p=>p.id===program.persona_id);
  const coAnchor = personas.find(p=>p.id===program.co_anchor_persona_id);
  const mode = CO_ANCHOR_MODES.find(m=>m.id===program.co_anchor_mode);

  return (
    <Card style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:14, flex:1 }}>
        <div style={{ width:44, height:44, borderRadius:10, background:'#f1f5f9',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
          {ct.icon}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, color:'#0f172a', marginBottom:2 }}>{program.name}</div>
          <div style={{ fontSize:12, color:'#64748b', marginBottom:6 }}>
            {DAYS[program.day_of_week]} · {program.start_time} – {program.end_time}
          </div>

          {/* Persona badges */}
          {persona && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              <span style={{ fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:8,
                background:'#eef2ff', color:'#6366f1', border:'1px solid #c7d2fe' }}>
                🎙 {persona.voice_name}
              </span>
              {coAnchor && (
                <span style={{ fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:8,
                  background:'#f0fdf4', color:'#16a34a', border:'1px solid #bbf7d0' }}>
                  + {coAnchor.voice_name}
                </span>
              )}
              {program.co_anchor_mode === 'human_ai' && (
                <span style={{ fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:8,
                  background:'#fffbeb', color:'#92400e', border:'1px solid #fde68a' }}>
                  🎙🤖 Human+AI
                </span>
              )}
              {mode && persona && (coAnchor || program.co_anchor_mode==='human_ai') && (
                <span style={{ fontSize:10, color:'#94a3b8' }}>{mode.label}</span>
              )}
            </div>
          )}
        </div>
      </div>
      <div style={{ display:'flex', gap:8, flexShrink:0 }}>
        <Btn size='sm' variant='ghost' onClick={()=>onEdit(program)}>Edit</Btn>
        <Btn size='sm' variant='danger' onClick={()=>onDelete(program.id)}>Delete</Btn>
      </div>
    </Card>
  );
}


// ─── Human+AI Live Panel (with mic capture) ──────────────────────────────────

function HumanAIPanel({ station, personas, onClose }) {
  const [personaId, setPersonaId] = React.useState('');
  const [humanName, setHumanName] = React.useState('Host');
  const [task, setTask] = React.useState('run_segment');
  const [generating, setGenerating] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);

  // Mic state
  const [recording, setRecording] = React.useState(false);
  const [transcript, setTranscript] = React.useState('');
  const [latestChunk, setLatestChunk] = React.useState('');
  const [chunkCount, setChunkCount] = React.useState(0);
  const mediaRecorderRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const chunkIntervalRef = React.useRef(null);
  const chunksRef = React.useRef([]);

  const BASE = process.env.REACT_APP_API_URL || 'https://groovie-business-api-production.up.railway.app/api/v1';

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Clear previous session
      const token = localStorage.getItem('broadcast_token');
      await fetch(`${BASE}/broadcast/co-anchor/human-ai/transcript`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      setTranscript(''); setLatestChunk(''); setChunkCount(0);

      const startChunk = () => {
        chunksRef.current = [];
        const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mr;

        mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        mr.onstop = async () => {
          if (chunksRef.current.length === 0) return;
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const form = new FormData();
          form.append('audio_chunk', blob, 'chunk.webm');
          try {
            const token = localStorage.getItem('broadcast_token');
            const res = await fetch(`${BASE}/broadcast/co-anchor/human-ai/transcribe`, {
              method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
            });
            if (res.ok) {
              const data = await res.json();
              if (data.transcript) {
                setTranscript(data.transcript);
                setLatestChunk(data.latest_chunk || '');
                setChunkCount(data.chunk_count || 0);
              }
            }
          } catch(e) { console.warn('[Human+AI] Chunk send failed', e); }
        };

        mr.start();
        // Record 12s chunks
        setTimeout(() => { if (mr.state === 'recording') mr.stop(); }, 12000);
      };

      startChunk();
      // Send a new chunk every 15s (overlap for continuity)
      chunkIntervalRef.current = setInterval(startChunk, 15000);
      setRecording(true);
    } catch(e) {
      setError('Mic access denied. Allow microphone permissions and try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (chunkIntervalRef.current) clearInterval(chunkIntervalRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setRecording(false);
  };

  React.useEffect(() => { return () => stopRecording(); }, []); // eslint-disable-line

  const generate = async () => {
    if (!personaId || !transcript.trim()) return;
    setGenerating(true); setResult(null); setError(null);
    try {
      const res = await api.post('/broadcast/co-anchor/human-ai/respond', {
        persona_id: parseInt(personaId),
        human_name: humanName,
        last_human_transcript: transcript,
        task,
        ai_role: 'co-host',
        station_id: station?.azuracast_station_id,
      });
      setResult(res);
    } catch(e) { setError(e.message); }
    finally { setGenerating(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:560, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'18px 24px', borderBottom:'1px solid #f1f5f9', display:'flex',
          alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, color:'#0f172a' }}>🎙🤖 Human + AI Live</div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>Mic → Whisper → AI responds in ~8s</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'#64748b' }}>✕</button>
        </div>
        <div style={{ padding:24 }}>
          {/* Config */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:5 }}>AI Persona</div>
              <select value={personaId} onChange={e=>setPersonaId(e.target.value)}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0',
                  borderRadius:8, fontSize:13, background:'#fff' }}>
                <option value="">— Select —</option>
                {personas.map(p=><option key={p.id} value={p.id}>{p.voice_name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:5 }}>Your On-Air Name</div>
              <input value={humanName} onChange={e=>setHumanName(e.target.value)}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0',
                  borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
            </div>
          </div>

          {/* Mic control */}
          <div style={{ background: recording ? '#fef2f2' : '#f8fafc', borderRadius:12,
            padding:20, marginBottom:16, textAlign:'center', border:`2px solid ${recording ? '#fca5a5' : '#e2e8f0'}` }}>
            <button onClick={recording ? stopRecording : startRecording}
              style={{ background: recording ? '#dc2626' : '#6366f1', color:'#fff',
                border:'none', borderRadius:50, width:64, height:64, fontSize:28,
                cursor:'pointer', marginBottom:10, boxShadow: recording ? '0 0 0 8px rgba(220,38,38,0.2)' : 'none',
                transition:'all 0.2s' }}>
              {recording ? '⏹' : '🎙'}
            </button>
            <div style={{ fontSize:13, fontWeight:600, color: recording ? '#dc2626' : '#374151' }}>
              {recording ? 'Recording… click to stop' : 'Click to start mic'}
            </div>
            {recording && chunkCount > 0 && (
              <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>
                {chunkCount} chunk{chunkCount!==1?'s':''}  transcribed
              </div>
            )}
          </div>

          {/* Transcript display */}
          {transcript && (
            <div style={{ background:'#f8fafc', borderRadius:10, padding:14, marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#374151', marginBottom:6 }}>
                LIVE TRANSCRIPT {latestChunk && <span style={{ color:'#6366f1' }}>· Latest: "{latestChunk.slice(0,60)}…"</span>}
              </div>
              <div style={{ fontSize:12, color:'#475569', lineHeight:1.6, maxHeight:80, overflowY:'auto' }}>
                {transcript}
              </div>
            </div>
          )}

          {/* Task selector */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>AI Response Task</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {['run_segment','tell_joke','news_toss','weather_hit','sign_off'].map(t=>(
                <button key={t} onClick={()=>setTask(t)} style={{
                  padding:'5px 12px', borderRadius:8, fontSize:11, fontWeight:600, cursor:'pointer',
                  border:`2px solid ${task===t ? '#6366f1' : '#e2e8f0'}`,
                  background: task===t ? '#eef2ff' : '#fff',
                  color: task===t ? '#6366f1' : '#374151' }}>
                  {t.replace(/_/g,' ')}
                </button>
              ))}
            </div>
          </div>

          <button onClick={generate}
            disabled={!personaId || !transcript.trim() || generating}
            style={{ width:'100%', padding:'12px 0', borderRadius:10, border:'none',
              background: (!personaId||!transcript.trim()||generating) ? '#e2e8f0' : '#6366f1',
              color: (!personaId||!transcript.trim()||generating) ? '#94a3b8' : '#fff',
              fontWeight:700, fontSize:13, cursor:'pointer' }}>
            {generating ? 'Generating AI response…' : '⚡ Generate AI Response'}
          </button>

          {error && <div style={{ marginTop:10, padding:10, background:'#fef2f2',
            borderRadius:8, fontSize:12, color:'#dc2626' }}>{error}</div>}

          {result && (
            <div style={{ marginTop:14, background:'#f8fafc', borderRadius:10, padding:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:8 }}>
                ✅ {result.persona_name} · {result.duration_seconds}s
              </div>
              <div style={{ fontSize:12, color:'#475569', fontStyle:'italic', lineHeight:1.6, marginBottom:10 }}>
                "{result.script}"
              </div>
              {result.audio_url?.startsWith('http') && (
                <audio controls src={result.audio_url} style={{ width:'100%' }} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Programs() {
  const station = getStation();
  const [programs, setPrograms] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editProgram, setEditProgram] = useState(null);
  const [showHumanAI, setShowHumanAI] = useState(false);
  const [filterDay, setFilterDay] = useState('');
  const [filterType, setFilterType] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/radio-programs').catch(()=>({programs:[]})),
      api.get('/broadcast/dbp/personas').catch(()=>({personas:[]})),
    ]).then(([prog, pers]) => {
      setPrograms(prog.programs || []);
      setPersonas(pers.personas || []);
    }).finally(()=>setLoading(false));
  };

  useEffect(()=>{ load(); }, []);

  const deleteProgram = async (id) => {
    if (!window.confirm('Delete this program slot?')) return;
    try {
      await api.del(`/radio-programs/${id}`);
      setPrograms(prev=>prev.filter(p=>p.id!==id));
    } catch(e) { alert(e.message); }
  };

  const filtered = programs.filter(p => {
    if (filterDay !== '' && p.day_of_week !== parseInt(filterDay)) return false;
    if (filterType && p.content_type !== filterType) return false;
    return true;
  });

  // Group by day
  const byDay = DAYS.map((day, idx) => ({
    day, idx,
    programs: filtered.filter(p=>p.day_of_week===idx)
      .sort((a,b)=>a.start_time.localeCompare(b.start_time)),
  })).filter(g => g.programs.length > 0 || filterDay==='');

  const personaCount = programs.filter(p=>p.content_type==='persona').length;
  const coAnchorCount = programs.filter(p=>p.co_anchor_persona_id || p.co_anchor_mode==='human_ai').length;

  return (
    <div style={{ maxWidth:1000, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:24, fontWeight:800, color:'#0f172a', margin:0 }}>Program Schedule</h1>
          <p style={{ fontSize:13, color:'#64748b', margin:'4px 0 0' }}>
            Manage broadcast slots · Assign personas · Configure co-anchoring
          </p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <Btn onClick={()=>setShowHumanAI(true)} variant='ghost'>🎙🤖 Human+AI Live</Btn>
          <Btn onClick={()=>setShowCreate(true)}>+ New Slot</Btn>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        {[
          { label:'Total Slots', value:programs.length, icon:'📅' },
          { label:'Persona Slots', value:personaCount, icon:'🤖' },
          { label:'Co-Anchor Active', value:coAnchorCount, icon:'🎙' },
          { label:'Personas Available', value:personas.filter(p=>p.status==='active').length, icon:'✅' },
        ].map(s=>(
          <Card key={s.label} style={{ textAlign:'center', padding:16 }}>
            <div style={{ fontSize:24, marginBottom:4 }}>{s.icon}</div>
            <div style={{ fontSize:22, fontWeight:800, color:'#0f172a' }}>{s.value}</div>
            <div style={{ fontSize:11, color:'#64748b' }}>{s.label}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20 }}>
        <select value={filterDay} onChange={e=>setFilterDay(e.target.value)}
          style={{ padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:9,
            fontSize:13, background:'#fff', cursor:'pointer' }}>
          <option value="">All Days</option>
          {DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}
        </select>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)}
          style={{ padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:9,
            fontSize:13, background:'#fff', cursor:'pointer' }}>
          <option value="">All Types</option>
          {CONTENT_TYPES.map(ct=><option key={ct.id} value={ct.id}>{ct.icon} {ct.label}</option>)}
        </select>
        {(filterDay!==''||filterType) && (
          <Btn size='sm' variant='ghost' onClick={()=>{setFilterDay('');setFilterType('');}}>Clear</Btn>
        )}
      </div>

      {/* Schedule */}
      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>Loading schedule…</div>
      ) : programs.length === 0 ? (
        <Card style={{ textAlign:'center', padding:60 }}>
          <div style={{ fontSize:48, marginBottom:16 }}>📅</div>
          <div style={{ fontSize:18, fontWeight:700, color:'#0f172a', marginBottom:8 }}>No program slots yet</div>
          <div style={{ fontSize:13, color:'#64748b', marginBottom:24 }}>
            Create your first slot — assign music, talk, news, or a DBP persona.
          </div>
          <Btn onClick={()=>setShowCreate(true)}>Create First Slot</Btn>
        </Card>
      ) : (
        <div style={{ display:'grid', gap:20 }}>
          {byDay.filter(g=>g.programs.length>0).map(g=>(
            <div key={g.idx}>
              <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:10,
                display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:'#6366f1', display:'inline-block' }} />
                {g.day}
                <span style={{ fontSize:11, color:'#94a3b8', fontWeight:400 }}>
                  {g.programs.length} slot{g.programs.length!==1?'s':''}
                </span>
              </div>
              <div style={{ display:'grid', gap:10 }}>
                {g.programs.map(p=>(
                  <ProgramCard key={p.id} program={p} personas={personas}
                    onEdit={setEditProgram} onDelete={deleteProgram} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {(showCreate || editProgram) && (
        <ProgramModal
          station={station || {}}
          personas={personas}
          editProgram={editProgram}
          onClose={()=>{ setShowCreate(false); setEditProgram(null); }}
          onSaved={load}
        />
      )}
      {showHumanAI && (
        <HumanAIPanel station={station} personas={personas} onClose={()=>setShowHumanAI(false)} />
      )}
    </div>
  );
}
